"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useInternalSearch = exports.InternalSearchProvider = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _patternLibraryUtils = require("@site/pattern-library-utils");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/**
 * Internal Search Context for managing search-related state
 * This context is separate from the external SiteSearchContext and is used
 * internally within the unified search component and its hooks.
 */
const InternalSearchContext = /*#__PURE__*/(0, _react.createContext)(null);

/**
 * Custom hook to use the internal search context
 * @returns {Object} The internal search context value
 * @throws {Error} If used outside of InternalSearchProvider
 */
const useInternalSearch = () => {
  const context = (0, _react.useContext)(InternalSearchContext);
  if (!context) {
    throw new Error('useInternalSearch must be used within an InternalSearchProvider');
  }
  return context;
};

/**
 * Internal Search Provider that manages all search-related state
 * @param {Object} props - Provider props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.autocompleteEndpoint - Autocomplete API endpoint
 * @param {string} props.resultsEndpoint - Results API endpoint
 * @param {string} props.domainSearchEndpoint - Domain search endpoint
 * @param {Object} props.searchSerp - Search SERP configuration
 * @param {boolean} props.fuzzy - Whether to use fuzzy search
 * @param {string} props.helpSource - Help source parameter
 * @param {string} props.source - Source parameter
 * @param {number} props.searchStringMinLength - Minimum search string length
 * @returns {React.ReactElement} The provider component
 */
exports.useInternalSearch = useInternalSearch;
const InternalSearchProvider = ({
  children,
  autocompleteEndpoint,
  resultsEndpoint,
  domainSearchEndpoint,
  searchSerp,
  fuzzy,
  helpSource,
  source,
  searchStringMinLength,
  appKeyHeader = 'fos-pattern-library'
}) => {
  // Search state
  const [searchValue, setSearchValue] = (0, _patternLibraryUtils.useQueryParam)('q', '');
  const [value, setValue] = (0, _react.useState)('');
  const [step, setStep] = (0, _react.useState)('initial');
  const [results, setResults] = (0, _react.useState)(null);
  const [markdown, setMarkdown] = (0, _react.useState)();
  const [availableTags, setAvailableTags] = (0, _react.useState)([]);
  const [activeFilter, setActiveFilter] = (0, _react.useState)('all');
  const [isLoading, setIsLoading] = (0, _react.useState)(true);
  const [domainSearchUrl, setDomainSearchUrl] = (0, _react.useState)(domainSearchEndpoint);
  const [suggestedList, setSuggestedList] = (0, _react.useState)([]);
  const [list, setList] = (0, _react.useState)([]);
  const [isActive, setIsActive] = (0, _react.useState)(false);
  const [trackingCustomProperties, setTrackingCustomProperties] = (0, _react.useState)({});
  const [currentDomainSearch, setCurrentDomainSearch] = (0, _react.useState)(searchSerp);
  const [containerHeight, setContainerHeight] = (0, _react.useState)('auto');

  // Constants
  const abortMessage = 'Aborted: Request cancelled to avoid duplication of search queries.';
  const contextValue = {
    // State
    searchValue,
    setSearchValue,
    value,
    setValue,
    step,
    setStep,
    results,
    setResults,
    markdown,
    setMarkdown,
    availableTags,
    setAvailableTags,
    activeFilter,
    setActiveFilter,
    isLoading,
    setIsLoading,
    domainSearchUrl,
    setDomainSearchUrl,
    suggestedList,
    setSuggestedList,
    list,
    setList,
    isActive,
    setIsActive,
    trackingCustomProperties,
    setTrackingCustomProperties,
    currentDomainSearch,
    setCurrentDomainSearch,
    containerHeight,
    setContainerHeight,
    // Configuration
    autocompleteEndpoint,
    resultsEndpoint,
    domainSearchEndpoint,
    searchSerp,
    fuzzy,
    helpSource,
    source,
    searchStringMinLength,
    abortMessage,
    appKeyHeader
  };
  return /*#__PURE__*/_react.default.createElement(InternalSearchContext.Provider, {
    value: contextValue
  }, children);
};
exports.InternalSearchProvider = InternalSearchProvider;
InternalSearchProvider.propTypes = {
  children: _propTypes.default.node.isRequired,
  autocompleteEndpoint: _propTypes.default.string.isRequired,
  resultsEndpoint: _propTypes.default.string.isRequired,
  domainSearchEndpoint: _propTypes.default.string.isRequired,
  searchSerp: _propTypes.default.object,
  fuzzy: _propTypes.default.bool,
  helpSource: _propTypes.default.string,
  source: _propTypes.default.string,
  searchStringMinLength: _propTypes.default.number,
  appKeyHeader: _propTypes.default.string
};